import {
  type PropsWithChildren,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import {
  getRememberVisitorValue,
  useLocation,
  useStorage
} from '@elfsight/widget-utils';
import { useSettings } from '@wu/widget-core';
import { VerificationContext } from './VerificationContext';

const addProtocolToUrlIfNeeded = (rawUrl) =>
  /^(http(s?)|tel|mailto|SMS|skype):/.test(rawUrl)
    ? rawUrl
    : `https://${rawUrl}`;

export function VerificationProvider({ children }: PropsWithChildren) {
  const {
    widgetId,
    editMode,
    rememberDuration,
    underAgeAction,
    underAgeRedirectUrl
  } = useSettings();

  // Миграция со старого ключа в localStorage на новый. До этого в ключе отсутствовал widgetId
  const [migratedAllow, setMigratedAllow] = useState(null);
  useLayoutEffect(() => {
    try {
      const storageKey = 'AgeVerification.allow';
      const oldStorageValue = localStorage.getItem(storageKey);

      if (!oldStorageValue) {
        return;
      }

      const oldValueObj = JSON.parse(oldStorageValue);
      const hasFields =
        oldValueObj?.timestamp !== undefined &&
        oldValueObj?.value !== undefined;

      if (hasFields) {
        localStorage.setItem(`${storageKey}.${widgetId}`, oldStorageValue);
        setMigratedAllow(oldValueObj.value);
      }

      localStorage.removeItem(storageKey);
      localStorage.removeItem('AgeVerification.firstCommit');
    } catch (error) {
      console.error('Failed to migrate stored allow:', error);
    }
  }, [widgetId]);

  const rememberDurationValue = getRememberVisitorValue(rememberDuration);
  const [storedAllow, { set: setStoredAllow, isExpired }] = useStorage(
    'allow',
    null
  );

  const currentAllow = migratedAllow !== null ? migratedAllow : storedAllow;
  const hasMadeChoiceRef = useRef(false);
  const isWidgetVisible = (() => {
    if (rememberDurationValue === -1) {
      return false;
    }

    if (rememberDurationValue === 0) {
      return !hasMadeChoiceRef.current;
    }

    return isExpired(rememberDurationValue);
  })();

  const allow = isWidgetVisible ? null : currentAllow;
  const show = allow !== true;
  const { href } = useLocation();

  useEffect(() => {
    if (isWidgetVisible && currentAllow !== null) {
      setStoredAllow(null);
      setMigratedAllow(null);
    }
  }, [isWidgetVisible, currentAllow, href, setStoredAllow]);

  useEffect(() => {
    if (editMode && !show) {
      const reopenTimeout = setTimeout(() => {
        hasMadeChoiceRef.current = false;
        setStoredAllow(null);
        setMigratedAllow(null);
      }, 1000);
      return () => clearTimeout(reopenTimeout);
    }
  }, [editMode, show, setStoredAllow]);

  const verificationContextValue = useMemo(
    () => ({
      allow,
      show,
      toggleAllow: (allowed) => {
        hasMadeChoiceRef.current = true;
        setMigratedAllow(null);
        setStoredAllow(allowed);

        if (!allowed && underAgeAction === 'redirect') {
          const processedUnderAgeRedirectUrl =
            addProtocolToUrlIfNeeded(underAgeRedirectUrl);
          if (editMode) {
            window.open(processedUnderAgeRedirectUrl, '_blank');
          } else {
            window.location.replace(processedUnderAgeRedirectUrl);
          }
        }
      }
    }),
    [allow, show, editMode, underAgeAction, underAgeRedirectUrl, setStoredAllow]
  );

  return (
    <VerificationContext.Provider value={verificationContextValue}>
      {children}
    </VerificationContext.Provider>
  );
}
